import React, { useEffect, useState } from "react";
import axios from "axios";
import { Tree, TreeNode } from "react-organizational-chart";
import { ADMIN_USER_TREE } from "../../hooks/APIHooks";
import { FaUserCircle, FaCheckCircle, FaTimesCircle, FaUsers } from "react-icons/fa";
import LogoLoader from "../../components/LogoLoader/LogoLoader";

const renderTree = (user, isFocusMode, onFocusChange) => {
  const isVerified = user.pannel_access === "True";
  
  return (
    <TreeNode
      label={
        <div 
          onClick={() => isFocusMode && onFocusChange(user)}
          className={`inline-block p-2 rounded-xl border transition-all transform hover:scale-105 shadow-lg min-w-[120px] ${
            isFocusMode ? "cursor-pointer active:scale-95 hover:border-secondary" : ""
          } ${
            isVerified 
              ? "bg-secondary/10 border-secondary/30 text-white" 
              : "bg-red-500/10 border-red-500/30 text-red-200"
          }`}
        >
          <div className="flex flex-col items-center gap-1">
            <div className="relative">
              <FaUserCircle size={20} className={isVerified ? "text-secondary" : "text-red-400"} />
              {isVerified ? (
                <FaCheckCircle className="absolute -bottom-1 -right-1 text-green-500 bg-primary rounded-full" size={10} />
              ) : (
                <FaTimesCircle className="absolute -bottom-1 -right-1 text-red-500 bg-primary rounded-full" size={10} />
              )}
            </div>
            <p className="text-[10px] font-black uppercase truncate max-w-[110px]">{user.name}</p>
            <p className="text-[8px] opacity-60 font-medium">{user.phonenumber}</p>
            
            <div className="flex gap-2 mt-1 pt-1 border-t border-white/5 w-full justify-center">
              <div className="text-center">
                <p className="text-[7px] uppercase opacity-50 font-bold leading-none">Ref</p>
                <p className="text-[9px] font-black text-secondary leading-none">{user.reference_total}</p>
              </div>
              <div className="text-center border-l border-white/5 pl-2">
                <p className="text-[7px] uppercase opacity-50 font-bold leading-none">Coins</p>
                <p className="text-[9px] font-black text-secondary leading-none">₹{user.digital_currency}</p>
              </div>
            </div>
            {isFocusMode && user.children?.length > 0 && (
              <p className="text-[7px] text-secondary font-black animate-bounce mt-1">CLICK TO DRILL DOWN</p>
            )}
          </div>
        </div>
      }
    >
      {/* In focus mode, we only render children if this IS the root of the focus, 
          but renderTree is recursive, so we handle it in the main component logic 
          by only passing children for the focus root. */}
      {!isFocusMode && user.children?.map((child) => renderTree(child, false, onFocusChange))}
    </TreeNode>
  );
};

const ReferralTree = () => {
  const [treeData, setTreeData] = useState(null);
  const [focusUser, setFocusUser] = useState(null);
  const [viewMode, setViewMode] = useState("full"); // "full" or "focus"
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(ADMIN_USER_TREE)
      .then((res) => {
        setTreeData(res.data);
        setFocusUser(res.data);
      })
      .catch((err) => console.error("Error fetching tree", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LogoLoader />;

  return (
    <div className="bg-primary-100 min-h-screen pb-24">
      {/* Legend & Header */}
      <div className="p-4 bg-primary border-b border-white/5 sticky top-0 z-50 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-white italic uppercase tracking-tighter flex items-center gap-2">
            <FaUsers className="text-secondary" /> Network Tree
          </h2>
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10">
            <button 
              onClick={() => setViewMode("full")}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${viewMode === "full" ? "bg-secondary text-primary" : "text-gray-400"}`}
            >
              Full View
            </button>
            <button 
              onClick={() => setViewMode("focus")}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${viewMode === "focus" ? "bg-secondary text-primary" : "text-gray-400"}`}
            >
              Focus View
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-secondary shadow-[0_0_6px_#ffcc00]"></span>
              <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">Verified</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_6px_#ef4444]"></span>
              <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">Unverified</span>
            </div>
          </div>
          {viewMode === "focus" && focusUser !== treeData && (
            <button 
              onClick={() => setFocusUser(treeData)}
              className="text-[9px] font-black text-secondary underline uppercase italic"
            >
              Reset to Root
            </button>
          )}
        </div>
      </div>

      <div className="overflow-auto p-8 no-scrollbar">
        <div className="min-w-max">
          {treeData && (
            <Tree
              lineWidth={"1.5px"}
              lineColor={"rgba(255, 204, 0, 0.2)"}
              lineBorderRadius={"12px"}
              label={
                viewMode === "full" ? (
                  <div className="inline-block p-4 rounded-2xl bg-secondary text-primary shadow-2xl border-4 border-primary transform transition-transform hover:scale-110">
                    <FaUserCircle size={32} className="mx-auto mb-1" />
                    <p className="font-black text-xs uppercase italic tracking-tighter">Admin Roots</p>
                    <p className="text-[10px] font-bold">{treeData.name}</p>
                  </div>
                ) : (
                  <div className="inline-block p-4 rounded-2xl bg-primary border-2 border-secondary text-white shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-1">
                      <FaUserCircle size={20} className="text-secondary opacity-20" />
                    </div>
                    <p className="text-[8px] font-black text-secondary uppercase tracking-widest leading-none mb-1">Focus Root</p>
                    <p className="font-black text-sm uppercase italic tracking-tighter">{focusUser?.name}</p>
                    <p className="text-[9px] text-gray-500">{focusUser?.phonenumber}</p>
                  </div>
                )
              }
            >
              {viewMode === "full" 
                ? treeData.children?.map((child) => renderTree(child, false, setFocusUser))
                : focusUser?.children?.map((child) => renderTree(child, true, setFocusUser))
              }
            </Tree>
          )}
        </div>
      </div>
      
      <div className="text-center px-10 space-y-2">
        <p className="text-gray-500 text-[10px] italic">
          {viewMode === "full" 
            ? "Scroll to navigate the entire network structure." 
            : "Click on any child node to drill down and see their direct referrals."}
        </p>
      </div>
    </div>
  );
};

export default ReferralTree;
